import { FREE_QUESTIONS_PROTOCOL, isTrade, JobTrade, MAX_QUESTIONS, QuestionKey, Trade, } from "@gigsync/protocol";
import { recall, summary } from "../memory/recall.js";
import { askModel } from "./question-model.js";
const T = Trade.enum;
const Q = QuestionKey.enum;
/**
 * The worker's agent's standard questions, per trade: what it asks when there is no model, the model fails, or the
 * guards drop everything it wrote. Keys only: each phone shows them in its owner's language (`questionText`).
 */
export const QUESTIONS = {
    [T.electrician]: [Q.problem],
    [T.plumber]: [Q.problem],
    [T.maid]: [Q.rooms, Q.pets],
    [T.carpenter]: [Q.problem],
    [T.ac_technician]: [Q.problem],
    // Rooms and who buys the paint set the estimate's scope (ADR 0030); a household gives the rooms up front.
    [T.painter]: [Q.rooms, Q.labour_only],
    [T.cook]: [Q.people, Q.meals, Q.pets],
    [T.driver]: [Q.route, Q.hours],
    // ADR 0047: a skill outside the trades has no price drivers, so only what and how long; when and budget are Find's.
    [JobTrade.enum.other]: [Q.job, Q.duration],
};
/** A detail one of the worker's own rules reads: the city band's scope rules (price) or their "show me first" list. */
function ruleReads(rules, trade, key) {
    const band = rules.rates?.bands.find((b) => b.trade === trade);
    return Boolean(band?.scope.some((s) => s.key === key) || rules.ask_me?.includes(key));
}
/** True when every phone of the household understands model-written questions; false when unsure. */
async function understands(ctx, r) {
    try {
        return await ctx.speaks(r.engagement.counterpart, FREE_QUESTIONS_PROTOCOL);
    }
    catch {
        return false;
    }
}
/**
 * What the worker's agent asks before quoting. With a model, and a household whose phones all read written
 * questions, it asks what the model wrote (after the guards) plus the standard details a rule of the worker's still
 * reads, so the price stays rule-based. Otherwise, or when nothing the model wrote survives, the standard questions
 * for the trade that the household has not answered yet.
 */
export async function questionsFor(ctx, rules, r) {
    const { trade, details, need } = r.request;
    const missing = QUESTIONS[trade].filter((key) => !details[key]);
    const standard = missing.map((key) => ({ key }));
    // Other work (ADR 0047) gets the generic questions only: no memory or model prompt is keyed to a skill yet.
    if (!isTrade(trade) || !ctx.model || !(await understands(ctx, r)))
        return standard;
    const read = missing.filter((key) => ruleReads(rules, trade, key));
    const counterpart = r.engagement.counterpart;
    const [known, memory] = await Promise.all([recall(ctx, trade, counterpart), summary(ctx, trade, counterpart)]);
    const remembered = Object.keys(known.person?.details ?? {});
    const given = Object.keys(details);
    const knownKeys = QuestionKey.options.filter((k) => [...given, ...remembered, ...read].includes(k));
    const max = MAX_QUESTIONS - read.length;
    const { kept } = await askModel(ctx.model, { trade, need, details, memory, known: knownKeys, max });
    return kept.length === 0 ? standard : [...read.map((key) => ({ key })), ...kept];
}
//# sourceMappingURL=ask.js.map